import React, { useEffect, useRef, useState } from 'react';
import CurrencyApi from '../../api/CurrencyApi';
import {
  Button,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Table,
} from 'react-bootstrap';
import { FaTrash, FaExclamationTriangle, FaEllipsisV, FaTimes, FaPlay, FaDownload } from 'react-icons/fa';
import PubSub from 'pubsub-js';
import DataApi from '../../api/DataApi';
import { exportToExcel } from '../../utils/excelExport.js';
import { formatCalculatedValue, normalizeCalculatedDisplayConfig, formatCurrency, toDisplayPrimitive, formatDate, formatDateTime } from '../../utils/formatUtils';
import {
  buildExchangeRateLookupFromCurrencyList,
  resolveEffectiveExchangeRate,
} from '../../utils/reportCurrencyAggregate';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import jwt_decode from 'jwt-decode';
import EditLayoutModal from '../layout/EditLayoutModal';
import FilePreviewModal from './FilePreviewModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import SalesforceToast from './SalesforceToast';
import ImportDataModal from './ImportDataModal';
import TelecmiRecordingPlayerModal from './TelecmiRecordingPlayerModal';
import { downloadTelecmiRecordingFile } from './TelecmiRecordingAudio';
import './css/ResizableTable.css';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';

const ResizableTable = ({
  data = [],
  columns = [],
  loading = false,
  error = null,
  searchTerm = '',
  onSearchChange = () => { },
  currentPage = 1,
  totalRecords = 0,
  recordsPerPage = 20,
  onPageChange = () => { },
  sortBy = '',
  sortOrder = 'asc',
  onSort = () => { },
  showSerialNumber = true,
  showActions = true,
  actionsRenderer = null,
  actions = [], // Array of action objects: { label, icon, href, onClick, target }
  className = '',
  tableClassName = 'table-theme',
  onRowClick = null,
  rowClassName = '',
  emptyMessage = 'No records found',
  showSearch = true,
  showPagination = true,
  searchPlaceholder = 'Search records...',
  title = '',
  headerActions = [], // Array of header action objects: { label, icon, variant, onClick, disabled, className }
  selectedRows = [],
  onRowSelectionChange = () => { },
  exportFilename = 'table_export',
  exportSheetName = 'Data',
  onNewRecord = null,
  showNewButton = false,
  objectName = null,
  sourcePage = 'table',
  onRefresh = null,
  recordId = null,
  targetField = null,
  parentObjectName = null, // Source object name for related list context
  allObjectFields = [], // All available fields for import mapping
  recordLinkField = 'name', // Field used to render hyperlink to record detail
  triggerImport = false, // Prop to trigger import modal from parent
  onImportTriggered = null, // Callback when import is triggered
  onShowToast = null, // (message, variant) => {} — use parent's AppToast when provided
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizeColumn, setResizeColumn] = useState(null);
  const [internalSelectedRows, setInternalSelectedRows] = useState([]);
  const [internalSortBy, setInternalSortBy] = useState('');
  const [internalSortOrder, setInternalSortOrder] = useState('asc');
  const isResizingRef = useRef(false);

  // Modal state
  const [showNewRecordModal, setShowNewRecordModal] = useState(false);
  const [showEditRecordModal, setShowEditRecordModal] = useState(false);
  const [showCloneRecordModal, setShowCloneRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [cloningRecord, setCloningRecord] = useState(null);

  // File Preview Modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFileRecord, setPreviewFileRecord] = useState(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  // Bulk delete state (result shown in same modal: counts + error reasons)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null); // { deleted, total, errors: [{ id, error }], apiError?: string }

  // Single record delete state (result shown in same modal: success or error message)
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteResultMessage, setDeleteResultMessage] = useState(null); // { variant: 'success'|'danger', text: string } | null
  const [isSingleDeleteInProgress, setIsSingleDeleteInProgress] = useState(false);
  const [deleteChildrenChecked, setDeleteChildrenChecked] = useState(false);

  // Bulk delete: option to also delete child records
  const [bulkDeleteChildrenChecked, setBulkDeleteChildrenChecked] = useState(false);

  // Toast state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);
  const [telecmiPlayer, setTelecmiPlayer] = useState({
    show: false,
    filename: null,
  });

  console.log('recordId prop:', recordId);

  // Resolve currency with priority: token currency (user currency or company currency) > default
  const resolveCurrency = (fallbackCurrency = 'INR') => {
    // Check currency from JWT token
    // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
    // So it will be user.currency if available, otherwise company.currency
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwt_decode(token);
        // Currency in token is: user.currency if available, otherwise company.currency
        const tokenCurrency = decoded.currency;
        if (tokenCurrency) {
          return tokenCurrency;
        }
      }
    } catch (error) {
      console.error('Error decoding token for currency:', error);
    }

    // Fallback to default
    return fallbackCurrency;
  };

  // Base currency + current exchange rates for list/related list (same as DetailLayout / reports)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const raw = res?.data?.data ?? res?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const baseRow = list.find((r) => r.is_base_currency === true);
        setBaseCurrencyCode(baseRow?.currency_code ? String(baseRow.currency_code).trim() : null);
        setExchangeRateLookup(buildExchangeRateLookupFromCurrencyList(list));
      } catch (e) {
        if (!cancelled) {
          setBaseCurrencyCode(null);
          setExchangeRateLookup(null);
        }
      }
      try {
        const s = await getMultiCurrencySettings();
        if (!cancelled) setMultiCurrencyEnabled(!!s.multiCurrencyEnabled);
      } catch {
        if (!cancelled) setMultiCurrencyEnabled(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Sync ref with state
  useEffect(() => {
    isResizingRef.current = isResizing;
  }, [isResizing]);

  // Sync internal selected rows with prop
  useEffect(() => {
    setInternalSelectedRows(selectedRows);
  }, [selectedRows]);

  // Handle import trigger from parent
  useEffect(() => {
    if (triggerImport && objectName) {
      setShowImportModal(true);
      // Notify parent that import was triggered (reset the trigger)
      if (onImportTriggered) {
        // Use setTimeout to ensure state update happens after modal opens
        setTimeout(() => {
          onImportTriggered();
        }, 0);
      }
    }
  }, [triggerImport, objectName, onImportTriggered]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (activeDropdown && !event.target.closest('.dropdown')) {
        setActiveDropdown(null);
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activeDropdown]);

  // Handle dropdown toggle
  const handleDropdownToggle = (recordId, isOpen) => {
    if (isOpen) {
      setActiveDropdown(recordId);
    } else {
      setActiveDropdown(null);
    }
  };

  // Close all dropdowns when clicking outside
  const closeAllDropdowns = () => {
    setActiveDropdown(null);
  };

  // Handle new record modal
  const handleNewRecord = () => {
    console.log('handleNewRecord called');
    console.log('onNewRecord prop:', onNewRecord);
    console.log('objectName prop:', objectName);
    console.log('recordId prop:', recordId);
    console.log('targetField prop:', targetField);
    console.log('queryParams to be passed:', {
      targetField: targetField,
      targetId: recordId,
    });
    console.log('showNewRecordModal state:', showNewRecordModal);

    if (onNewRecord) {
      console.log('Using onNewRecord callback');
      onNewRecord();
    } else {
      console.log('Opening new record modal');
      setShowNewRecordModal(true);
    }
  };

  // Safely extract record id (supports object { value } and primitive)
  const getRecordIdValue = record => {
    if (!record) return '';
    if (record.id && typeof record.id === 'object' && record.id !== null) {
      return record.id.value ?? '';
    }
    if (record.id !== undefined && record.id !== null) {
      return record.id;
    }
    if (record.__recordId) {
      return record.__recordId;
    }
    return '';
  };

  // Handle view record
  const handleViewRecord = record => {
    console.log('handleViewRecord called with record:', record);
    const idValue = getRecordIdValue(record);
    if (!idValue) return;
    const targetUrl = `/view/${objectName || 'object'}/${idValue}`;
    if (window && typeof window.location !== 'undefined') {
      window.location.href = targetUrl;
    } else {
      window.open(targetUrl, '_self');
    }
  };

  // Handle view attachment (for preview)
  const handleViewAttachment = record => {
    console.log('handleViewAttachment called with record:', record);
    setPreviewFileRecord(record);
    setShowPreviewModal(true);
  };

  // Handle close preview modal
  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewFileRecord(null);
  };

  // Handle edit record modal
  const handleEditRecord = record => {
    console.log('handleEditRecord called with record:', record);
    const idValue = getRecordIdValue(record);
    console.log('record.id value:', idValue);
    console.log('objectName:', objectName);
    console.log('targetField:', targetField);
    console.log('recordId:', recordId);
    // Preserve editing record with normalized id shape for modal
    setEditingRecord({
      ...record,
      id: { ...(record.id || {}), value: idValue },
    });
    setShowEditRecordModal(true);
  };

  // Handle clone record modal
  const handleCloneRecord = record => {
    console.log('handleCloneRecord called with record:', record);
    const idValue = getRecordIdValue(record);
    // Store the record with id so EditLayout can fetch and pre-fill the data
    setCloningRecord({
      ...record,
      id: { ...(record.id || {}), value: idValue },
    });
    setShowCloneRecordModal(true);
  };

  // Handle delete record — open confirmation modal
  const handleDeleteRecord = record => {
    const idValue = getRecordIdValue(record);
    if (!idValue) {
      console.log('# [Toast] Single delete - no ID');
      if (onShowToast) onShowToast('No ID found for this record.', 'danger');
      else setToast({ show: true, message: 'No ID found for this record.', type: 'error' });
      return;
    }
    setRecordToDelete(record);
    setDeleteResultMessage(null);
    setShowSingleDeleteModal(true);
  };

  // Called when user confirms delete in the modal; result (success/error) is shown in the same modal
  const confirmDeleteRecord = async () => {
    if (!recordToDelete) return;
    const idValue = getRecordIdValue(recordToDelete);
    setIsSingleDeleteInProgress(true);

    try {
      const dataApi = new DataApi('object');
      const query = deleteChildrenChecked ? '?cascadeDeleteChildren=true' : '';
      const response = await dataApi.delete(`/${objectName}/${idValue}${query}`);
      if (response.data.success) {
        setDeleteResultMessage({ variant: 'success', text: 'Record deleted successfully!' });
        if (onRefresh) setTimeout(() => onRefresh(), 300);
      } else {
        const data = response?.data;
        const msg =
          (typeof data?.message === 'string' && data.message) ||
          (typeof data?.error === 'string' && data.error) ||
          'Failed to delete record.';
        setDeleteResultMessage({ variant: 'danger', text: msg });
      }
    } catch (error) {
      const data = error.response?.data;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        error.message ||
        'Error deleting record. Please try again.';
      setDeleteResultMessage({ variant: 'danger', text: msg });
    } finally {
      setIsSingleDeleteInProgress(false);
    }
  };

  // Handle download record (for attachments)
  const handleDownloadRecord = async record => {
    console.log('handleDownloadRecord called with record:', record);
    const idValue = getRecordIdValue(record);
    console.log('ID value extracted:', idValue);

    if (!idValue) {
      console.error('No ID value found for record');
      return;
    }

    try {
      // Get the filename from the record (backend handles extension properly)
      const fileName = record.file_name?.value || 'download';
      const fileType = record.file_type?.value || '';

      // Backend already handles extension duplication, but we add it if missing
      const filename = fileType && !fileName.includes('.')
        ? `${fileName}.${fileType}`
        : fileName;

      console.log('Filename for download:', filename);

      // Use DataApi for download
      console.log('Using DataApi for download...');
      const dataApi = new DataApi('attachments');

      // DataApi with responseType: 'blob' to handle binary data correctly
      const response = await dataApi.get(`/download/${idValue}`, {
        responseType: 'blob', // This is critical for binary data!
      });

      console.log('Response received:', response);
      console.log('Response data type:', response.data instanceof Blob);

      // The response.data is already a proper Blob with correct MIME type from backend
      const blob = response.data;
      if (!blob || blob.size === 0) {
        console.error('Download failed: received empty file');
        return;
      }

      console.log('Blob:', blob);
      console.log('Blob size:', blob.size);
      console.log('Blob type:', blob.type);

      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL after a short delay to ensure download completes
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      console.log('Download completed successfully');
    } catch (error) {
      console.error('Error downloading file:', error);
      // alert(`Failed to download file: ${error.message}`);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setShowNewRecordModal(false);
    setShowEditRecordModal(false);
    setShowCloneRecordModal(false);
    setEditingRecord(null);
    setCloningRecord(null);
  };

  // Handle modal save success
  const handleModalSaveSuccess = savedData => {
    console.log('Record saved successfully:', savedData);
    const isCloneOperation = !!cloningRecord;

    setShowNewRecordModal(false);
    setShowEditRecordModal(false);
    setShowCloneRecordModal(false);
    setEditingRecord(null);
    setCloningRecord(null);

    // Show success notification for clone operations (matching DetailLayout behavior)
    if (isCloneOperation) {
      setToast({
        show: true,
        message: 'Record cloned successfully!',
        type: 'success'
      });
    }

    // Refresh the data if callback provided
    if (onRefresh) {
      onRefresh();
    }
  };

  // Check if checkboxes should be shown (when delete or export actions are present)
  const shouldShowCheckboxes = () => {
    return headerActions.some(
      action =>
        action.label.toLowerCase().includes('delete') ||
        action.label.toLowerCase().includes('export')
    );
  };

  // Handle row selection
  const handleRowSelection = (recordId, isSelected) => {
    let newSelectedRows;
    if (isSelected) {
      newSelectedRows = [...internalSelectedRows, recordId];
    } else {
      newSelectedRows = internalSelectedRows.filter(id => id !== recordId);
    }
    setInternalSelectedRows(newSelectedRows);
    onRowSelectionChange(newSelectedRows);
  };

  // Handle select all
  const handleSelectAll = isSelected => {
    const allIds = data.map(record => getRecordIdValue(record));
    const newSelectedRows = isSelected ? allIds : [];
    setInternalSelectedRows(newSelectedRows);
    onRowSelectionChange(newSelectedRows);
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    const exportData =
      selectedRows.length > 0
        ? data.filter(record => selectedRows.includes(getRecordIdValue(record)))
        : data;

    exportToExcel(
      exportData,
      columns,
      selectedRows,
      exportFilename,
      exportSheetName
    );
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "No Selection",
        message: "Please select records to delete."
      });
      return;
    }
    setBulkDeleteResult(null);
    setShowDeleteConfirmModal(true);
  };

  // Confirm bulk delete; result (counts + reasons) shown in same modal
  const confirmBulkDelete = async () => {
    if (selectedRows.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "No Selection",
        message: "No records selected for deletion."
      });
      return;
    }

    setIsDeleting(true);
    setBulkDeleteResult(null);

    try {
      const dataApi = new DataApi('object');
      const response = await dataApi.deleteWithBody(`/${objectName}/bulk-delete`, {
        ids: selectedRows,
        options: {
          batchSize: 50,
          continueOnError: true,
          cascadeDeleteChildren: bulkDeleteChildrenChecked
        }
      });

      if (response?.data?.success) {
        const result = response.data.data;
        setBulkDeleteResult({
          deleted: result.deleted ?? 0,
          total: result.total ?? selectedRows.length,
          errors: result.errors ?? [],
          apiError: null
        });
      } else {
        const data = response?.data;
        const msg =
          (typeof data?.message === 'string' && data.message) ||
          (typeof data?.error === 'string' && data.error) ||
          'Failed to delete records. Please try again.';
        setBulkDeleteResult({
          deleted: 0,
          total: selectedRows.length,
          errors: [],
          apiError: msg
        });
      }
    } catch (error) {
      const data = error.response?.data;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        error.message ||
        'Error deleting records. Please try again.';
      setBulkDeleteResult({
        deleted: 0,
        total: selectedRows.length,
        errors: [],
        apiError: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Close bulk delete modal; if result was shown, clear selection and refresh list (no full page reload)
  const cancelBulkDelete = () => {
    if (bulkDeleteResult) {
      setInternalSelectedRows([]);
      onRowSelectionChange?.([]);
      if (onRefresh) onRefresh();
    }
    setBulkDeleteResult(null);
    setBulkDeleteChildrenChecked(false);
    setShowDeleteConfirmModal(false);
  };

  // Handle Import functionality
  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleImportModalClose = () => {
    setShowImportModal(false);
  };

  // Infer field type when not explicitly provided
  const inferFieldType = (fieldName, fieldObj) => {
    const lower = (fieldName || '').toLowerCase();
    if (lower.includes('created') || lower.includes('modified'))
      return 'datetime';
    if (lower.includes('date')) return 'date';
    if (
      lower.includes('amount') ||
      lower.includes('price') ||
      lower.includes('cost')
    )
      return 'currency';
    if (lower.endsWith('id')) return 'uuid';
    const value =
      fieldObj && typeof fieldObj === 'object' ? fieldObj.value : fieldObj;
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !isNaN(Number(value))
    )
      return 'number';
    return 'text';
  };

  // Comparable value for sorting
  const getComparableValue = (record, fieldName) => {
    if (fieldName === 'id') {
      return getRecordIdValue(record) || '';
    }
    const field = record?.[fieldName];
    if (field === null || field === undefined) return null; // Use null for blank values

    // Extract raw value properly
    let rawValue;
    if (typeof field === 'object' && field !== null) {
      rawValue = field.value; // This will be undefined if field.value doesn't exist
    } else {
      rawValue = field;
    }

    // Check if the raw value is blank (null, undefined, or empty string)
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null; // Use null for blank values
    }

    const type =
      typeof field === 'object' && field.type
        ? field.type
        : inferFieldType(fieldName, field);

    if (type === 'date' || type === 'datetime') {
      const d = new Date(rawValue);
      const t = d.getTime();
      return isNaN(t) ? null : t; // Use null for invalid dates
    }
    if (
      type === 'number' ||
      type === 'integer' ||
      type === 'decimal' ||
      type === 'currency'
    ) {
      const n = parseFloat(rawValue);
      return isNaN(n) ? null : n; // Use null for invalid numbers
    }
    if (type === 'boolean' || type === 'checkbox') {
      const v =
        rawValue === true ||
        rawValue === 'true' ||
        rawValue === 1 ||
        rawValue === '1';
      return v ? 1 : 0;
    }
    return String(rawValue).toLowerCase();
  };

  // Effective sort coming from props or internal
  const effectiveSortBy = sortBy || internalSortBy;
  const effectiveSortOrder = sortOrder || internalSortOrder;

  // Handle header sort click
  const handleHeaderSort = field => {
    if (!field) return;
    setInternalSortBy(prev => {
      if (prev === field) {
        setInternalSortOrder(order => (order === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setInternalSortOrder('asc');
      return field;
    });
    // Call parent callback if provided (non-blocking)
    if (onSort) onSort(field);
  };

  // Sorted data for rendering
  const displayedData = React.useMemo(() => {
    if (!effectiveSortBy) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      const aVal = getComparableValue(a, effectiveSortBy);
      const bVal = getComparableValue(b, effectiveSortBy);

      // Handle null values (blank values) - they should always appear at the end
      if (aVal === null && bVal === null) return 0; // Both are blank, maintain order
      if (aVal === null) return 1; // a is blank, b is not - a goes after b
      if (bVal === null) return -1; // b is blank, a is not - b goes after a

      // Both values are not null, proceed with normal comparison
      if (effectiveSortOrder === 'asc')
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
    return copy;
  }, [data, effectiveSortBy, effectiveSortOrder]);

  // Handle column resizing
  const handleMouseDown = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('Resize started for column:', columnIndex);

    setIsResizing(true);
    setResizeColumn(columnIndex);
    isResizingRef.current = true;

    const startX = e.clientX;
    const startWidth = columnWidths[columnIndex] || 150;

    const handleMouseMove = e => {
      if (isResizingRef.current) {
        const deltaX = e.clientX - startX;
        const newWidth = Math.max(50, startWidth + deltaX); // Minimum 50px width

        console.log('Resizing column', columnIndex, 'to width:', newWidth);

        setColumnWidths(prev => ({
          ...prev,
          [columnIndex]: newWidth,
        }));
      }
    };

    const handleMouseUp = () => {
      console.log('Resize ended for column:', columnIndex);
      setIsResizing(false);
      setResizeColumn(null);
      isResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Helper function to get currency from record (so currency columns show correct symbol
  // even when the Currency field is not on the related list layout)
  const getRecordCurrency = (record) => {
    if (!record) return null;

    // Check for currency field in various formats (top-level or field object)
    const currencyField = record.currency || record.currency_code || record.Currency;
    if (currencyField) {
      // Handle both object format { value: 'USD' } and direct value
      if (typeof currencyField === 'object' && currencyField !== null) {
        const val = currencyField.value ?? currencyField.displayValue ?? currencyField;
        if (val != null && val !== '') return val;
      } else {
        return currencyField;
      }
    }

    // Fallback: when currency is not on layout, backend may still send it at top-level
    // (record.currency); if not, try record.fields from APIs that return fields array
    if (Array.isArray(record.fields)) {
      const field = record.fields.find(
        f => f && String(f.fieldName || f.field_name || '').toLowerCase() === 'currency'
      );
      if (field) {
        const v = field.value ?? field.displayValue;
        if (v != null && v !== '') return v;
      }
    }
    return null;
  };

  /** Normalize API values that may be primitives or field-shaped objects { value, displayValue, ... } */
  const unwrapScalarFromRecord = (val) => {
    if (val == null || val === '') return null;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      const inner = val.value ?? val.displayValue;
      if (inner == null || inner === '') return null;
      if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
        return inner.value ?? inner.displayValue ?? null;
      }
      return inner;
    }
    return val;
  };

  const getRecordExchangeRate = (record) => {
    if (!record) return null;
    const er = record.exchange_rate ?? record.exchangeRate;
    const top = unwrapScalarFromRecord(er);
    if (top != null && top !== '') return top;
    if (Array.isArray(record.fields)) {
      const f = record.fields.find(
        (x) => x && String(x.fieldName || x.field_name || '').toLowerCase() === 'exchange_rate'
      );
      if (f) {
        const v = unwrapScalarFromRecord(f.value ?? f.displayValue);
        if (v != null && v !== '') return v;
      }
    }
    return null;
  };

  const appendBaseCurrencyEquivalent = (formattedMain, numValue, record, recordCurrency, decimalPlaces) => {
    if (!multiCurrencyEnabled) return formattedMain;
    if (!record || !baseCurrencyCode || !recordCurrency) return formattedMain;
    if (
      String(recordCurrency).trim().toUpperCase() === String(baseCurrencyCode).trim().toUpperCase()
    ) {
      return formattedMain;
    }
    const rawExchangeRate = getRecordExchangeRate(record);
    const rowRate = Number(rawExchangeRate);
    const effectiveRate = resolveEffectiveExchangeRate(
      recordCurrency,
      rowRate,
      exchangeRateLookup
    );
    if (!Number.isFinite(effectiveRate) || effectiveRate <= 0) return formattedMain;
    const opts = {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };
    const baseEquivalent = numValue / effectiveRate;
    return `${formattedMain} (${formatCurrency(baseEquivalent, baseCurrencyCode, opts)} ${baseCurrencyCode})`;
  };

  // Format field value for display
  const formatFieldValue = (field, record = null) => {
    if (
      !field ||
      field.value === null ||
      field.value === undefined ||
      field.value === ''
    ) {
      return '&nbsp;';
    }

    // Use displayValue for lookup fields and UUID fields (ensure we never render [object Object])
    if (field.displayValue !== undefined && field.displayValue !== null && field.displayValue !== '') {
      const safe = toDisplayPrimitive(field.displayValue);
      if (safe !== null && safe !== undefined && safe !== '') return String(safe);
    }

    if ((field.type || '').toLowerCase() === 'calculated') {
      return formatCalculatedValue(field.value, field.displayConfig);
    }

    // Handle boolean fields
    if (field.type === 'checkbox' || field.type === 'boolean') {
      return field.value === true || field.value === 'true' || field.value === 1
        ? 'Yes'
        : 'No';
    }

    // Handle currency fields (case-insensitive check)
    if ((field.type || '').toLowerCase() === 'currency') {
      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        // Use scale (decimal places) if provided, otherwise default to 2
        const decimalPlaces = (field.scale !== undefined && field.scale !== null && field.scale !== '')
          ? parseInt(field.scale, 10)
          : 2;
        // Resolve currency: record currency > field currency > token currency (user or company) > default
        const recordCurrency = record ? getRecordCurrency(record) : null;
        const currencyCode = recordCurrency || field.currencyCode || resolveCurrency('INR');
        const formatted = formatCurrency(numValue, currencyCode, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        });
        return appendBaseCurrencyEquivalent(formatted, numValue, record, recordCurrency, decimalPlaces);
      }
    }

    // Handle rollup summary fields with display_config (case-insensitive check)
    if ((field.type || '').toLowerCase() === 'rollupsummary') {
      const displayConfig = field.displayConfig || field.display_config;
      let parsedConfig = {};
      if (displayConfig) {
        if (typeof displayConfig === 'string') {
          try {
            parsedConfig = JSON.parse(displayConfig);
          } catch (e) {
            parsedConfig = {};
          }
        } else {
          parsedConfig = displayConfig || {};
        }
      }
      const rollupConfig = parsedConfig.rollupSummary || {};
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();

      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        if (outputType === 'currency') {
          const decimalPlaces = (field.scale !== undefined && field.scale !== null && field.scale !== '')
            ? parseInt(field.scale, 10)
            : 2;
          // Resolve currency: record currency > field currency > token currency (user or company) > default
          const recordCurrency = record ? getRecordCurrency(record) : null;
          const currencyCode = recordCurrency || field.currencyCode || resolveCurrency('INR');
          const formatted = formatCurrency(numValue, currencyCode, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
          });
          return appendBaseCurrencyEquivalent(formatted, numValue, record, recordCurrency, decimalPlaces);
        } else if (outputType === 'date') {
          try {
            const date = new Date(field.value);
            if (!isNaN(date.getTime())) {
              return formatDate(field.value);
            }
          } catch (e) {
            console.warn('Invalid date value:', field.value);
          }
        } else if (outputType === 'datetime') {
          try {
            const datetime = new Date(field.value);
            if (!isNaN(datetime.getTime())) {
              return formatDateTime(field.value);
            }
          } catch (e) {
            console.warn('Invalid datetime value:', field.value);
          }
        } else {
          // Default to number formatting
          if (field.scale !== undefined && field.scale !== null && field.scale !== '') {
            const decimalPlaces = parseInt(field.scale, 10);
            return numValue.toLocaleString('en-IN', {
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces
            });
          } else {
            return numValue.toLocaleString('en-IN');
          }
        }
      }
    }

    // Handle number fields
    if (
      field.type === 'number' ||
      field.type === 'integer' ||
      field.type === 'decimal'
    ) {
      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        // Use scale (decimal places) if provided
        if (field.scale !== undefined && field.scale !== null && field.scale !== '') {
          const decimalPlaces = parseInt(field.scale, 10);
          return numValue.toLocaleString('en-IN', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
          });
        } else {
          return numValue.toLocaleString('en-IN');
        }
      }
    }

    // Handle date fields
    if (field.type === 'date' && field.value) {
      try {
        const date = new Date(field.value);
        if (!isNaN(date.getTime())) {
          return formatDate(field.value);
        }
      } catch (e) {
        console.warn('Invalid date value:', field.value);
      }
    }

    // Handle datetime fields
    if (field.type === 'datetime' && field.value) {
      try {
        const datetime = new Date(field.value);
        if (!isNaN(datetime.getTime())) {
          return formatDateTime(field.value);
        }
      } catch (e) {
        console.warn('Invalid datetime value:', field.value);
      }
    }

    // Handle time fields
    if (field.type === 'time' && field.value) {
      try {
        let timeValue = field.value;
        // If it's a full datetime string, extract just the time part
        if (timeValue.includes('T') || timeValue.includes(' ')) {
          const date = new Date(timeValue);
          if (!isNaN(date.getTime())) {
            return date.toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
          }
        } else if (timeValue.includes(':')) {
          // If it's already in HH:MM:SS format, format it nicely
          const parts = timeValue.split(':');
          return `${parts[0]}:${parts[1]}`;
        }
        return timeValue;
      } catch (e) {
        console.warn('Invalid time value:', field.value);
      }
    }

    // Handle URL fields
    if (field.type === 'url' && field.value) {
      return field.value;
    }

    // Default: return the value as string
    return String(field.value);
  };

  // Format field value with JSX (for links, etc.)
  const formatFieldValueWithJSX = (field, fieldName, record) => {
    const normalizedField =
      field && typeof field === 'object' && !Array.isArray(field)
        ? field
        : { value: field };
    const rawValue = normalizedField.value;
    const displayValue = normalizedField.displayValue ?? rawValue;
    const normalizedFieldName = (fieldName || '').toLowerCase();
    const normalizedRecordLinkField = (recordLinkField || 'name').toLowerCase();
    if (normalizedFieldName === normalizedRecordLinkField) {
      const recordIdValue = getRecordIdValue(record);
      if (recordIdValue) {
        const handleLinkClick = (event) => {
          event.preventDefault();
          handleViewRecord(record);
        };
        return (
          <a
            href={`/view/${objectName || 'object'}/${recordIdValue}`}
            className='table-link'
            onClick={handleLinkClick}
          >
            {displayValue}
          </a>
        );
      }
      return displayValue;
    }

    // TeleCMI: recording player field (preferred) + filename display-only field.
    // Must run BEFORE the empty-value early return because `recording` is a virtual field
    // and may not exist on the row payload.
    if (
      (objectName || '').toLowerCase() === 'telecmi_log' &&
      (normalizedFieldName === 'recording' || normalizedFieldName === 'file_name')
    ) {
      const extractFileNameFromRow = () => {
        const v =
          record?.file_name?.value ??
          record?.file_name?.displayValue ??
          record?.file_name ??
          record?.fileName ??
          null;
        return typeof v === 'string' ? v.trim() : null;
      };

      const rawStr =
        typeof rawValue === 'string' && rawValue.trim() !== ''
          ? rawValue.trim()
          : null;
      const filename = rawStr || extractFileNameFromRow();
      const isAudio =
        typeof filename === 'string' && /\.(mp3|wav|m4a)$/i.test(filename);

      if (normalizedFieldName === 'recording') {
        return isAudio ? (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={e => e.stopPropagation()}
            role="group"
            aria-label="Recording actions"
          >
            <button
              type="button"
              title="Play recording"
              aria-label="Play recording"
              onClick={e => {
                e.stopPropagation();
                setTelecmiPlayer({ show: true, filename });
              }}
              style={{
                border: 'none',
                background: '#e9ecef',
                padding: 8,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#212529',
              }}
            >
              <FaPlay size={14} />
            </button>
            <button
              type="button"
              title="Download recording"
              aria-label="Download recording"
              onClick={async e => {
                e.stopPropagation();
                await downloadTelecmiRecordingFile(filename);
              }}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 6,
                borderRadius: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#495057',
              }}
            >
              <FaDownload size={14} />
            </button>
          </div>
        ) : (
          ''
        );
      }
      // file_name is display-only (no player)
      return filename || '';
    }

    if (
      rawValue === null ||
      rawValue === undefined ||
      rawValue === ''
    ) {
      return '';
    }

    if ((normalizedField.type || '').toLowerCase() === 'calculated') {
      const normalizedConfig = normalizeCalculatedDisplayConfig(normalizedField.displayConfig);
      const returnType = normalizedConfig.returnType;
      const rawConfig = normalizedConfig.rawConfig || {};
      const rawCalculated = normalizedConfig.rawCalculated || {};
      const combinedConfig = { ...rawConfig, ...rawCalculated };
      const value = normalizedField.value;
      const formatted = formatCalculatedValue(value, normalizedField.displayConfig);

      if (returnType === 'url' && value) {
        return (
          <a
            href={String(value)}
            className='table-link'
            target='_blank'
            rel='noopener noreferrer'
          >
            {formatted || value}
          </a>
        );
      }

      if (returnType === 'image' && value) {
        const width = combinedConfig.width ?? 50;
        const height = combinedConfig.height ?? 50;
        return (
          <img
            src={value}
            alt={normalizedField.label || 'Calculated image'}
            style={{
              maxWidth: width,
              maxHeight: height,
              objectFit: combinedConfig.objectFit ?? 'cover',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = '<span style="color: #999; font-size: 11px;">No image</span>';
            }}
          />
        );
      }

      if (returnType === 'barcode' && value) {
        return (
          <Barcode
            value={String(value)}
            format={combinedConfig.format ?? 'CODE128'}
            width={combinedConfig.width ?? 2}
            height={combinedConfig.height ?? 80}
            displayValue={combinedConfig.displayValue ?? false}
          />
        );
      }

      if (returnType === 'qrcode' && value) {
        return (
          <QRCode
            value={String(value)}
            size={combinedConfig.size ?? 64}
            level={combinedConfig.errorCorrectionLevel ?? 'M'}
            style={{ maxWidth: '64px', height: 'auto' }}
          />
        );
      }

      return safeRender(formatted);
    }

    // Handle reference fields (lookup fields)
    if (normalizedField.isReference && normalizedField.displayValue) {
      return (
        <a
          href={`/view/${normalizedField.referenceObject || objectName || 'object'}/${normalizedField.value}`}
          className='table-link'
        >
          {normalizedField.displayValue}
        </a>
      );
    }

    // Handle the configured record link field - make it a hyperlink to the record detail page



    // Handle UUID fields - make them clickable links (except when a dedicated link field is used elsewhere)
    if (
      normalizedField.type === 'uuid' &&
      normalizedField.value &&
      !(normalizedFieldName === 'id' && normalizedRecordLinkField !== 'id')
    ) {
      return (
        <a
          href={`/view/${normalizedField.referenceObject || objectName || 'object'}/${normalizedField.value}`}
          className='table-link'
        >
          {normalizedField.displayValue || normalizedField.value}
        </a>
      );
    }

    // Handle URL fields
    if (normalizedField.type === 'url' && normalizedField.value) {
      return (
        <a href={normalizedField.value} className='table-link'>
          {normalizedField.value}
        </a>
      );
    }

    // Handle file size fields
    const isFileSizeField = normalizedFieldName && (
      normalizedFieldName === 'file_size' ||
      normalizedFieldName === 'filesize' ||
      normalizedFieldName === 'size' ||
      normalizedFieldName.endsWith('_size')
    );

    if (isFileSizeField && rawValue) {
      // Format file size helper function
      const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 Bytes';
        // Handle both number and string inputs
        const sizeInBytes = typeof bytes === 'string' ? parseFloat(String(bytes).replace(/,/g, '')) : bytes;
        if (isNaN(sizeInBytes) || sizeInBytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
        return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      };
      return formatFileSize(rawValue);
    }

    // Handle picklist fields with dynamic colored badges
    const isPicklistField = (normalizedField.type || '').toLowerCase() === 'picklist' ||
      (normalizedField.type || '').toLowerCase() === 'multipicklist';

    if (isPicklistField && rawValue) {
      // Generate a consistent color based on the value (hash-based)
      // Using professionally designed color combinations with proper contrast
      const getBadgeColor = (value) => {
        if (!value) return { bg: '#f5f5f5', color: '#6c757d' };

        // Create a simple hash from the value for consistent coloring
        let hash = 0;
        const str = String(value).toLowerCase();
        for (let i = 0; i < str.length; i++) {
          hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Professional color palette with proper contrast ratios (WCAG AA compliant)
        // Each pair has good visual harmony and readability
        const colors = [
          { bg: '#e8f5e9', color: '#2e7d32' }, // Light Green / Dark Green
          { bg: '#e3f2fd', color: '#1565c0' }, // Light Blue / Dark Blue
          { bg: '#fce4ec', color: '#c2185b' }, // Light Pink / Dark Pink
          { bg: '#fff3e0', color: '#e65100' }, // Light Orange / Dark Orange
          { bg: '#f3e5f5', color: '#6a1b9a' }, // Light Purple / Dark Purple
          { bg: '#e0f7fa', color: '#00838f' }, // Light Cyan / Dark Cyan
          { bg: '#fff9c4', color: '#f57f17' }, // Light Yellow / Dark Yellow
          { bg: '#ffebee', color: '#c62828' }, // Light Red / Dark Red
          { bg: '#e8eaf6', color: '#283593' }, // Light Indigo / Dark Indigo
          { bg: '#f1f8e9', color: '#558b2f' }, // Light Lime / Dark Lime
          { bg: '#fbe9e7', color: '#bf360c' }, // Light Deep Orange / Dark Deep Orange
          { bg: '#ede7f6', color: '#4527a0' }, // Light Deep Purple / Dark Deep Purple
        ];

        const colorIndex = Math.abs(hash) % colors.length;
        return colors[colorIndex];
      };

      const badgeColors = getBadgeColor(rawValue);

      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: badgeColors.bg,
            color: badgeColors.color,
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            whiteSpace: 'nowrap',
            textTransform: 'capitalize'
          }}
        >
          {displayValue || rawValue}
        </span>
      );
    }

    // Default: return formatted value (pass record for currency resolution)
    return formatFieldValue(normalizedField, record);
  };

  // Safe render function
  const safeRender = value => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    if (React.isValidElement(value)) {
      return value;
    }
    return String(value);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startRecord = (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  // Render pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => onPageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key='start-ellipsis' />);
      }
    }

    // Visible pages
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => onPageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key='end-ellipsis' />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  if (loading) {
    return (
      <div className={`resizable-table ${className}`}>
        <div className='text-center p-4'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`resizable-table ${className}`}>
        <div className='alert alert-danger' role='alert'>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`resizable-table ${className}`}>
      {/* Records Count with Search and Actions on the same line */}
      {data.length > 0 && (
        <div className='records-info mb-3' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p className='text-muted mb-0'>
            {searchTerm.trim()
              ? `Showing ${data.length} of ${totalRecords} filtered records`
              : `Showing ${startRecord} to ${endRecord} of ${totalRecords} records`}
          </p>

          {/* Search Bar and Header Actions on the right */}
          <div className='d-flex align-items-center' style={{ gap: 0 }}>
            {showSearch && (
              <div className='search-container' style={{ width: '250px', margin: 0, padding: 0 }}>
                <InputGroup style={{ margin: 0 }}>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderTopLeftRadius: '0.375rem',
                      borderBottomLeftRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0.75rem',
                      height: '38px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <i
                      className='fas fa-search'
                      style={{ color: '#6c757d', fontSize: '0.875rem' }}
                    ></i>
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={e => onSearchChange(e.target.value)}
                    style={{
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0.75rem',
                      height: '38px',
                    }}
                  />
                  {searchTerm && (
                    <InputGroup.Text
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderLeft: 'none',
                        borderTopRightRadius: '0',
                        borderBottomRightRadius: '0',
                        fontSize: '0.875rem',
                        padding: '0.5rem 0.75rem',
                        height: '38px',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => onSearchChange('')}
                      title="Clear search"
                    >
                      <FaTimes
                        style={{ color: '#6c757d', fontSize: '0.875rem' }}
                      />
                    </InputGroup.Text>
                  )}
                </InputGroup>
              </div>
            )}

            {(() => {
              // Filter visible actions first
              const visibleActions = headerActions.filter(action => {
                // Hide actions marked as hidden
                if (action.hidden) {
                  return false;
                }
                // Hide import action for email_log
                if (objectName?.toLowerCase() === 'email_log' && action.label.toLowerCase().includes('import')) {
                  return false;
                }
                return true;
              });

              // Only show dropdown if there are visible actions
              if (visibleActions.length === 0) {
                return null;
              }

              return (
                <div className='header-actions' style={{ position: 'relative', marginLeft: showSearch ? '-1px' : '0' }}>
                  <Dropdown onToggle={(isOpen) => { }} align="end">
                    <Dropdown.Toggle
                      variant="light"
                      className="no-caret"
                      style={{
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #d8dde6',
                        borderLeft: 'none',
                        borderTopLeftRadius: '0',
                        borderBottomLeftRadius: '0',
                        borderTopRightRadius: '0.375rem',
                        borderBottomRightRadius: '0.375rem',
                        backgroundColor: 'white',
                        color: '#6c757d',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '38px',
                        boxShadow: 'none',
                        outline: 'none'
                      }}
                      title="More Actions"
                    >
                      <FaEllipsisV size={16} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      align="end"
                      style={{
                        minWidth: '200px',
                        border: '1px solid #d8dde6',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        padding: '4px 0',
                        marginTop: '4px'

                      }}
                    >
                      {visibleActions.map((action, index) => (
                        <Dropdown.Item
                          key={index}
                          onClick={
                            action.label.toLowerCase().includes('delete')
                              ? handleBulkDelete
                              : action.label.toLowerCase().includes('export')
                                ? handleExportToExcel
                                : action.label.toLowerCase().includes('import')
                                  ? handleImportClick
                                  : action.label.toLowerCase().includes('new')
                                    ? handleNewRecord
                                    : action.onClick
                          }
                          disabled={action.disabled || (action.label.toLowerCase().includes('delete') && isDeleting)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 16px',
                            cursor: (action.disabled || (action.label.toLowerCase().includes('delete') && isDeleting)) ? 'not-allowed' : 'pointer',
                            opacity: (action.disabled || (action.label.toLowerCase().includes('delete') && isDeleting)) ? 0.6 : 1,
                            color: action.label.toLowerCase().includes('delete') ? '#dc3545' : 'inherit'
                          }}
                        >
                          {action.icon && <i className={action.icon} style={{ width: '16px' }}></i>}
                          <span>{action.label}</span>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Show search and actions even when no data (for empty state) */}
      {data.length === 0 && (showSearch || headerActions.length > 0) && (
        <div className='mb-3' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {showSearch && (
            <div className='search-container' style={{ width: '250px', margin: 0, padding: 0 }}>
              <InputGroup style={{ margin: 0 }}>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderRight: 'none',
                    borderTopLeftRadius: '0.375rem',
                    borderBottomLeftRadius: '0.375rem',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <i
                    className='fas fa-search'
                    style={{ color: '#6c757d', fontSize: '0.875rem' }}
                  ></i>
                </InputGroup.Text>
                <Form.Control
                  type='text'
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={e => onSearchChange(e.target.value)}
                  style={{
                    borderTopRightRadius: '0.375rem',
                    borderBottomRightRadius: '0.375rem',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem',
                    height: '38px',
                  }}
                />
                {searchTerm && (
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0.75rem',
                      height: '38px',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => onSearchChange('')}
                    title="Clear search"
                  >
                    <FaTimes
                      style={{ color: '#6c757d', fontSize: '0.875rem' }}
                    />
                  </InputGroup.Text>
                )}
              </InputGroup>
            </div>
          )}

          {(() => {
            const visibleActions = headerActions.filter(action => {
              if (action.hidden) return false;
              if (objectName?.toLowerCase() === 'email_log' && action.label.toLowerCase().includes('import')) {
                return false;
              }
              return true;
            });

            if (visibleActions.length === 0) {
              return null;
            }

            return (
              <div className='header-actions' style={{ marginLeft: showSearch ? '-1px' : '0' }}>
                {visibleActions
                  .filter(a => a.label.toLowerCase().includes('new'))
                  .map((action, i) => (
                    <button style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid rgb(216, 221, 230)',


                      borderRadius: '0.375rem',
                      backgroundColor: 'white',
                      color: '#6c757d',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '38px',
                      boxShadow: 'none',
                      outline: 'none'
                    }} key={i} onClick={handleNewRecord}>+ New</button>
                  ))}
                <Dropdown onToggle={(isOpen) => { }}>
                  <Dropdown.Toggle
                    variant="light"
                    className="no-caret"
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid rgb(216, 221, 230)',
                      borderRadius: '0.375rem',
                      backgroundColor: 'white',
                      color: '#6c757d',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '38px',
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    title="More Actions"
                  >
                    <FaEllipsisV size={16} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    style={{
                      minWidth: '200px',
                      border: '1px solid #d8dde6',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      padding: '4px 0',
                      marginTop: '4px'
                    }}
                  >
                    {visibleActions.map((action, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={
                          action.label.toLowerCase().includes('delete')
                            ? handleBulkDelete
                            : action.label.toLowerCase().includes('export')
                              ? handleExportToExcel
                              : action.label.toLowerCase().includes('import')
                                ? handleImportClick

                                : action.onClick
                        }
                        disabled={action.disabled || (action.label.toLowerCase().includes('delete') && isDeleting)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 16px',
                          cursor: (action.disabled || (action.label.toLowerCase().includes('delete') && isDeleting)) ? 'not-allowed' : 'pointer',
                          opacity: (action.disabled || (action.label.toLowerCase().includes('delete') && isDeleting)) ? 0.6 : 1,
                          color: action.label.toLowerCase().includes('delete') ? '#dc3545' : 'inherit'
                        }}
                      >
                        {action.icon && <i className={action.icon} style={{ width: '16px' }}></i>}
                        <span>{action.label}</span>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            );
          })()}
        </div>
      )}

      {/* Content - Table or Empty State */}
      {data.length > 0 ? (
        /* Table */
        <div
          className='table-responsive-wrapper'
          style={{
            maxHeight: 'calc(90vh + 100px)',
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            border: 'none',
            borderRadius: '0.375rem',
          }}
        >
          <div
            className='table-responsive'
            style={{
              minWidth: 'max-content',
              width: '100%',
            }}
          >
            <Table
              className={`records-table ${tableClassName}`}
              style={{
                tableLayout: 'fixed',
                width: '100%',
                minWidth: 'max-content',
                borderCollapse: 'separate',
                borderSpacing: 0,
              }}
            >
              <thead>
                <tr>
                  {showSerialNumber && (
                    <th
                      className='table-theme-header'
                      style={{
                        width: columnWidths[0] || 'auto',
                        minWidth: '50px',
                        position: 'relative',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      #
                      <div
                        className='resize-handle'
                        onMouseDown={e => handleMouseDown(e, 0)}
                      />
                    </th>
                  )}
                  {shouldShowCheckboxes() && (
                    <th
                      className='table-theme-header'
                      style={{
                        width: '40px',
                        minWidth: '40px',
                        position: 'relative',
                        textAlign: 'center',
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={
                          data.length > 0 && selectedRows.length === data.length
                        }
                        onChange={e => handleSelectAll(e.target.checked)}
                        style={{ cursor: 'pointer', margin: '0 auto', display: 'block' }}
                      />
                    </th>
                  )}
                  {columns.map((column, index) => {
                    // Calculate column index accounting for serial number (index 0) and checkbox (index 1 if serial shown, 0 if not)
                    const columnIndex = (showSerialNumber ? 1 : 0) + (shouldShowCheckboxes() ? 1 : 0) + index;
                    // Determine if this is a numeric field for right alignment
                    const fieldType = column.dataType || '';
                    const isNumeric = ['currency', 'number', 'decimal', 'integer', 'rollupsummary'].includes(fieldType?.toLowerCase());

                    return (
                      <th
                        key={index}
                        className='table-theme-header'
                        onClick={() =>
                          column.sortable !== false &&
                          handleHeaderSort(column.field)
                        }
                        style={{
                          cursor:
                            column.sortable !== false ? 'pointer' : 'default',
                          width:
                            columnWidths[columnIndex] || column.width || 'auto',
                          minWidth: '200px',
                          position: 'relative',
                          textAlign: column.align || (isNumeric ? 'right' : 'left'),
                        }}
                      >
                        {column.label}
                        {column.sortable !== false &&
                          effectiveSortBy === column.field && (
                            <i
                              className={`fas fa-sort-${effectiveSortOrder === 'asc' ? 'up' : 'down'} ms-1`}
                            ></i>
                          )}
                        <div
                          className='resize-handle'
                          onMouseDown={e => handleMouseDown(e, columnIndex)}
                        />
                      </th>
                    );
                  })}
                  {showActions && (
                    <th
                      className='table-theme-header'
                      style={{
                        width: columnWidths['actions'] || '120px',
                        position: 'relative',
                      }}
                    >
                      Actions
                      <div
                        className='resize-handle'
                        onMouseDown={e => handleMouseDown(e, 'actions')}
                      />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayedData.length > 0 ? (
                  displayedData.map((record, index) => (
                    <tr
                      key={
                        typeof record.id === 'string'
                          ? record.id
                          : `row-${index}`
                      }
                      className={`table-theme-row ${rowClassName}`}
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(record);
                        }
                      }}
                      style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                    >
                      {showSerialNumber && (
                        <td
                          style={{
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            width: columnWidths[0] || 'auto',
                            minWidth: '50px',
                            padding: '0.75rem 0.5rem',
                          }}
                        >
                          {(currentPage - 1) * recordsPerPage + index + 1}
                        </td>
                      )}
                      {shouldShowCheckboxes() && (
                        <td
                          style={{
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '40px',
                            width: '40px',
                          }}
                        >
                          <input
                            type='checkbox'
                            checked={selectedRows.includes(
                              getRecordIdValue(record)
                            )}
                            onChange={e => {
                              e.stopPropagation();
                              handleRowSelection(
                                getRecordIdValue(record),
                                e.target.checked
                              );
                            }}
                            style={{ cursor: 'pointer', margin: '0 auto', display: 'block' }}
                          />
                        </td>
                      )}
                      {columns.map((column, colIndex) => {
                        // Determine if this is a numeric field for right alignment
                        const field = record[column.field];
                        const fieldType = column.dataType || field?.type || '';
                        const isNumeric = ['currency', 'number', 'decimal', 'integer', 'rollupsummary'].includes(fieldType?.toLowerCase());

                        return (
                          <td
                            key={colIndex}
                            style={{
                              width:
                                columnWidths[
                                colIndex + (showSerialNumber ? 1 : 0) + (shouldShowCheckboxes() ? 1 : 0)
                                ] ||
                                column.width ||
                                'auto',
                              minWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: column.align || (isNumeric ? 'right' : 'left'),
                            }}
                          >
                            <div
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                textAlign: column.align || (isNumeric ? 'right' : 'left'),
                              }}
                            >
                              {safeRender(
                                formatFieldValueWithJSX(record[column.field], column.field, record)
                              )}
                            </div>
                          </td>
                        );
                      })}
                      {showActions && (
                        <td
                          style={{ position: 'relative', overflow: 'visible' }}
                        >
                          {actionsRenderer ? (
                            actionsRenderer(record)
                          ) : (
                            <div
                              style={{
                                position: 'relative',
                                overflow: 'visible',
                              }}
                            >
                              <Dropdown
                                drop='down'
                                onToggle={isOpen =>
                                  handleDropdownToggle(
                                    typeof record.id === 'string'
                                      ? record.id
                                      : `row-${index}`,
                                    isOpen
                                  )
                                }
                                show={
                                  activeDropdown ===
                                  (typeof record.id === 'string'
                                    ? record.id
                                    : `row-${index}`)
                                }
                              >
                                <Dropdown.Toggle
                                  variant='outline-secondary'
                                  size='sm'
                                  className="no-caret"
                                  id={`dropdown-${record.id || index}`}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid #dee2e6',
                                    backgroundColor: 'white',
                                    position: 'relative',
                                    zIndex:
                                      activeDropdown ===
                                        (typeof record.id === 'string'
                                          ? record.id
                                          : `row-${index}`)
                                        ? 1
                                        : 1,
                                  }}
                                >
                                  &#9660;
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  style={{
                                    zIndex:
                                      activeDropdown ===
                                        (typeof record.id === 'string'
                                          ? record.id
                                          : `row-${index}`)
                                        ? 10001
                                        : 9999,
                                    position: 'absolute',
                                    right: 0,
                                    left: 'auto',
                                    minWidth: '120px',
                                  }}
                                >
                                  {actions.length > 0 ? (
                                    actions.map((action, actionIndex) => {
                                      //console.log('🔍 Rendering action:', actionIndex, action);
                                      return (
                                        <Dropdown.Item
                                          key={actionIndex}
                                          href={
                                            action.href
                                              ? action.href(record)
                                              : undefined
                                          }
                                          onClick={
                                            action.onClick
                                              ? () => action.onClick(record)
                                              : action.action === 'view'
                                                ? objectName === 'attachment'
                                                  ? () => handleViewAttachment(record)
                                                  : () => handleViewRecord(record)
                                                : action.action === 'edit'
                                                  ? () => handleEditRecord(record)
                                                  : action.action === 'clone'
                                                    ? () => {
                                                      console.log('🔍 Clone action clicked for record:', record);
                                                      handleCloneRecord(record);
                                                    }
                                                    : action.action === 'delete'
                                                      ? () =>
                                                        handleDeleteRecord(record)
                                                      : action.action === 'download'
                                                        ? () =>
                                                          handleDownloadRecord(
                                                            record
                                                          )
                                                        : undefined
                                          }
                                        >
                                          {action.icon && (
                                            <i
                                              className={`${action.icon} me-2`}
                                            ></i>
                                          )}
                                          {action.label}
                                        </Dropdown.Item>
                                      );
                                    })
                                  ) : (
                                    // Default actions if no custom actions provided
                                    <>
                                      {objectName === 'attachment' ? (
                                        <>
                                          <Dropdown.Item
                                            onClick={() =>
                                              handleViewAttachment(record)
                                            }
                                          >
                                            <i className='fas fa-eye me-2'></i>
                                            View
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              handleDownloadRecord(record)
                                            }
                                          >
                                            <i className='fas fa-download me-2'></i>
                                            Download
                                          </Dropdown.Item>
                                        </>
                                      ) : (
                                        <>
                                          <Dropdown.Item
                                            href={`/view/${record.objectName || objectName || 'object'}/${record.id}`}
                                          >
                                            <i className='fas fa-eye me-2'></i>View
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              handleEditRecord(record)
                                            }
                                          >
                                            <i className='fas fa-edit me-2'></i>
                                            Edit
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              handleCloneRecord(record)
                                            }
                                          >
                                            <i className='fas fa-clone me-2'></i>
                                            Clone
                                          </Dropdown.Item>
                                        </>
                                      )}
                                    </>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        columns.length +
                        (showSerialNumber ? 1 : 0) +
                        (showActions ? 1 : 0)
                      }
                      className='text-center text-muted py-4'
                    >
                      <div style={{ padding: '2rem' }}>
                        <i
                          className='fas fa-inbox'
                          style={{
                            fontSize: '3rem',
                            color: '#6c757d',
                            marginBottom: '1rem',
                            display: 'block',
                          }}
                        ></i>
                        <h5
                          style={{ color: '#6c757d', marginBottom: '0.5rem' }}
                        >
                          {emptyMessage}
                        </h5>
                        {showNewButton && (
                          <div style={{ marginTop: '1rem' }}>
                            <Button
                              variant='primary'
                              onClick={handleNewRecord}
                              style={{
                                gap: '0.5rem',
                                fontWeight: '500',
                              }}
                            >
                              <i className='fas fa-plus'></i>
                              New Record
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div
          className='empty-state-container'
          style={{
            textAlign: 'center',
            padding: '3rem 2rem',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            marginTop: '1rem',
          }}
        >
          <i
            className='fas fa-inbox'
            style={{
              fontSize: '3rem',
              color: '#6c757d',
              marginBottom: '1rem',
              display: 'block',
            }}
          ></i>
          <h5 style={{ color: '#6c757d', marginBottom: '0.5rem' }}>
            {emptyMessage}
          </h5>
          {showNewButton && (
            <div style={{ marginTop: '1rem' }}>
              <Button
                variant='primary'
                onClick={handleNewRecord}
                style={{
                  gap: '0.5rem',
                  fontWeight: '500',
                }}
              >
                <i className='fas fa-plus'></i>
                New Record
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className='pagination-container mt-3'>
          <Pagination className='justify-content-center'>
            <Pagination.First
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {renderPaginationItems()}
            <Pagination.Next
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* New Record Modal */}
      {showNewRecordModal &&
        objectName &&
        (console.log('Rendering new record modal:', {
          showNewRecordModal,
          objectName,
        }) ||
          true) && (
          <EditLayoutModal
            show={showNewRecordModal}
            onHide={handleModalCancel}
            objectName={objectName}
            sourcePage={sourcePage}
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
            queryParams={{
              targetField: targetField,
              targetId: recordId,
              parentObjectName: parentObjectName,
            }}
          />
        )}

      {/* Edit Record Modal */}
      {showEditRecordModal &&
        objectName &&
        editingRecord &&
        (console.log('Rendering edit record modal:', {
          showEditRecordModal,
          objectName,
          editingRecord,
        }) ||
          true) && (
          <EditLayoutModal
            show={showEditRecordModal}
            onHide={handleModalCancel}
            objectName={objectName}
            id={editingRecord.id.value} // record ID for editing
            sourcePage={sourcePage}
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
            queryParams={{
              targetField: targetField,
              targetId: recordId,
              parentObjectName: parentObjectName,
            }}
          />
        )}

      {/* Clone Record Modal */}
      {showCloneRecordModal &&
        objectName &&
        cloningRecord &&
        (console.log('Rendering clone record modal:', {
          showCloneRecordModal,
          objectName,
          cloningRecord,
        }) ||
          true) && (
          <EditLayoutModal
            show={showCloneRecordModal}
            onHide={handleModalCancel}
            objectName={objectName}
            id={cloningRecord.id.value} // Pass id to load the record data
            isClone={true} // Special flag to indicate this is a clone operation
            sourcePage={sourcePage}
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
            queryParams={{
              targetField: targetField,
              targetId: recordId,
              parentObjectName: parentObjectName,
            }}
          />
        )}

      {/* File Preview Modal */}
      <FilePreviewModal
        show={showPreviewModal}
        onHide={handleClosePreviewModal}
        fileRecord={previewFileRecord}
      />

      <ImportDataModal
        show={showImportModal}
        onClose={handleImportModalClose}
        objectName={objectName}
        allObjectFields={allObjectFields}
        columns={columns}
        onRefresh={onRefresh}
      />

      {/* Bulk Delete Confirmation Modal; result (counts + reasons) shown in same dialog */}
      <Modal show={showDeleteConfirmModal} onHide={cancelBulkDelete} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {bulkDeleteResult ? (
              <>
                <FaTrash className="me-2 text-danger" />
                Bulk Delete Result
              </>
            ) : (
              <>
                <FaTrash className="me-2 text-danger" />
                Confirm Bulk Delete
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {bulkDeleteResult ? (
            <div className="bulk-delete-result">
              {bulkDeleteResult.apiError ? (
                <div className="alert alert-danger mb-0">
                  {bulkDeleteResult.apiError}
                </div>
              ) : (
                <>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="text-success">
                      <strong>Deleted:</strong> {bulkDeleteResult.deleted} of {bulkDeleteResult.total}
                    </div>
                    {bulkDeleteResult.errors.length > 0 && (
                      <div className="text-danger">
                        <strong>Failed:</strong> {bulkDeleteResult.errors.length}
                      </div>
                    )}
                  </div>
                  {bulkDeleteResult.errors.length > 0 && (
                    <div className="mt-2">
                      <strong className="text-muted">Reasons:</strong>
                      <ul className="list-unstyled mb-0 mt-1 small">
                        {bulkDeleteResult.errors.slice(0, 10).map((e, idx) => (
                          <li key={idx} className="text-danger">
                            Record {e.id}: {e.error}
                          </li>
                        ))}
                        {bulkDeleteResult.errors.length > 10 && (
                          <li className="text-muted">... and {bulkDeleteResult.errors.length - 10} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : isDeleting ? (
            <div className="d-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              <span>Deleting {selectedRows.length} record{selectedRows.length !== 1 ? 's' : ''}...</span>
            </div>
          ) : (
            <div className="text-center">
              <div className="mb-3">
                <FaExclamationTriangle size={64} className="text-warning mb-3" />
              </div>
              <h5>Are you sure you want to delete these records?</h5>
              <p className="text-muted">
                You are about to delete <strong>{selectedRows.length}</strong> record{selectedRows.length !== 1 ? 's' : ''} from <strong>{objectName || 'this object'}</strong>.
              </p>
              <div className="alert alert-warning">
                <strong><FaExclamationTriangle className="me-2" />Warning:</strong> This action cannot be undone. The selected records will be permanently deleted.
              </div>
              <div className="form-check mt-3 text-start">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="bulk-delete-children-checkbox"
                  checked={bulkDeleteChildrenChecked}
                  onChange={e => setBulkDeleteChildrenChecked(e.target.checked)}
                  disabled={isDeleting}
                />
                <label className="form-check-label" htmlFor="bulk-delete-children-checkbox">
                  Also delete related (child) records
                </label>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {bulkDeleteResult ? (
            <Button variant="primary" onClick={cancelBulkDelete}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={cancelBulkDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmBulkDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash className="me-2" />
                    Delete {selectedRows.length} Record{selectedRows.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Toast Notifications */}
      <SalesforceToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'success' })}
        duration={5000}
        position="top-center"
      />

      {/* Single record delete confirmation; success/error shown in same modal */}
      <DeleteConfirmationModal
        show={showSingleDeleteModal}
        onHide={() => {
          setShowSingleDeleteModal(false);
          setRecordToDelete(null);
          setDeleteResultMessage(null);
          setIsSingleDeleteInProgress(false);
          setDeleteChildrenChecked(false);
        }}
        onConfirm={confirmDeleteRecord}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        itemName={recordToDelete?.name?.value ?? recordToDelete?.id?.value ?? 'this record'}
        confirmText="Delete"
        cancelText="Cancel"
        resultMessage={deleteResultMessage?.text ?? null}
        resultVariant={deleteResultMessage?.variant ?? 'danger'}
        isDeleting={isSingleDeleteInProgress}
        showDeleteChildrenCheckbox
        deleteChildrenChecked={deleteChildrenChecked}
        onDeleteChildrenCheckboxChange={setDeleteChildrenChecked}
      />

      <TelecmiRecordingPlayerModal
        show={telecmiPlayer.show}
        filename={telecmiPlayer.filename}
        onHide={() => setTelecmiPlayer({ show: false, filename: null })}
      />
    </div>
  );
};

export default ResizableTable;
